import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/core/supabase/client';
import {
  borrarSesionOffline,
  elServidorRechazoLaSesion,
  guardarAutenticacionOnline,
  sesionOfflineUsable,
  venceLaGracia,
  type MotivoRechazo,
} from '@/core/auth/sesionOffline';
import { borrarCacheDeLecturas } from '@/core/offline/almacen';
import { alSaberQueNoHay, marcarCredencialSinConfirmar } from '@/core/red/conectividad';

interface AuthState {
  session: Session | null;
  loading: boolean;
  /**
   * Se está trabajando con la credencial local porque el servidor no contesta.
   *
   * Lo que se ve es lo que ya estaba en el teléfono, y lo que se cargue queda
   * en la cola. No es un estado de error: es el modo para el que se hizo esto.
   */
  sinConexion: boolean;
  /** Por qué NO se pudo entrar sin señal, cuando corresponde explicarlo. */
  motivo: MotivoRechazo | null;
  /** Hasta cuándo se puede seguir sin volver a conectarse. */
  graciaHasta: number | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Cuánto se espera a que el servidor conteste antes de abrir con lo local.
 *
 * `getSession()` no siempre falla rápido. Con el token vencido intenta
 * renovarlo, y una red que existe pero no llega a ningún lado —una raya de
 * señal en el campo— deja esa petición colgada sin devolver nunca. Sin este
 * límite, abrir la aplicación en esa situación se queda en «Cargando…» para
 * siempre: exactamente el arranque en frío sin señal para el que se hizo todo
 * esto.
 *
 * Ocho segundos es incómodo pero tolerable una vez; un cuelgue no se tolera
 * nunca. Y sólo se paga cuando de verdad no hay respuesta: con token válido la
 * consulta es puro almacenamiento local y vuelve al instante.
 *
 * En la aplicación empaquetada casi nunca se paga entero: si el sistema
 * operativo ya sabe que no hay red, la carrera la gana ese aviso y se abre al
 * instante con la credencial local. El límite queda para el caso feo de
 * verdad —hay antena, no hay servidor—, que nadie puede saber sin intentar.
 */
const ESPERA_MAXIMA = 8000;

/** El resultado del servidor, o `null` si no hubo forma de obtenerlo. */
async function preguntarAlServidor(): Promise<{ session: Session | null } | null> {
  let reloj: ReturnType<typeof setTimeout> | undefined;
  const sinServidor = alSaberQueNoHay();
  try {
    const vencido = new Promise<null>((listo) => {
      reloj = setTimeout(() => listo(null), ESPERA_MAXIMA);
    });
    const respuesta = await Promise.race([
      supabase.auth.getSession().then((r) => ({ session: r.data.session })),
      sinServidor.promesa.then(() => null),
      vencido,
    ]);
    return respuesta;
  } catch {
    return null;
  } finally {
    clearTimeout(reloj);
    sinServidor.cancelar();
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sinConexion, setSinConexionCrudo] = useState(false);
  const [motivo, setMotivo] = useState<MotivoRechazo | null>(null);
  const [graciaHasta, setGraciaHasta] = useState<number | null>(null);

  /**
   * Entrar y salir del modo de gracia, en un solo lugar.
   *
   * Estar en gracia significa que la aplicación corre con una credencial que
   * el servidor no confirmó. Mientras tanto, cada pedido que el servidor
   * rechace por permisos hay que saber explicarlo: sin esto, lo que sale en
   * pantalla es «permission denied for function search_clients», que no le
   * dice nada a nadie y esconde la única acción posible, que es volver a
   * entrar.
   *
   * Va envuelto y no repetido al lado de cada `setSinConexion`, que son once:
   * el día que alguien agregue el doce, la bandera lo acompaña sola.
   */
  const setSinConexion = useCallback((valor: boolean) => {
    setSinConexionCrudo(valor);
    marcarCredencialSinConfirmar(valor);
  }, []);

  useEffect(() => {
    let cancelado = false;

    /**
     * Resolver con qué sesión arranca la aplicación.
     *
     * El orden importa. Primero se le pregunta al servidor, siempre: la verdad
     * la tiene él. La credencial local es el plan B y sólo entra cuando el
     * servidor no está.
     */
    const resolver = async () => {
      const respuesta = await preguntarAlServidor();
      // Lo único que se mira de la respuesta es si TRAJO una sesión. Que la
      // llamada haya vuelto no dice nada: ver más abajo.
      const delServidor = respuesta?.session ?? null;

      if (cancelado) return;

      if (delServidor) {
        // Autenticación buena: se registra y el reloj de la gracia vuelve a
        // cero. Éste es el único lugar donde eso pasa.
        guardarAutenticacionOnline(delServidor);
        setSession(delServidor);
        setSinConexion(false);
        setMotivo(null);
        setGraciaHasta(venceLaGracia());
        setLoading(false);
        return;
      }

      // Sin sesión del servidor. Antes acá se decidía con
      //
      //     const parece = huboRed && navigator.onLine;
      //
      // y eso dejaba a alguien afuera en el campo. Dos errores encadenados:
      //
      //  1. **`getSession()` devolviendo `null` no prueba haber hablado con el
      //     servidor.** Lee el almacenamiento local y, si el token venció,
      //     intenta renovarlo; cuando ese intento falla por falta de red
      //     también devuelve `null`, y rápido. O sea que `huboRed` no
      //     significaba «hubo red»: significaba «la llamada volvió».
      //
      //  2. **`navigator.onLine` miente en el WebView de Android.** Es el
      //     motivo por el que existe `@capacitor/network` en este proyecto:
      //     en modo avión devolvía `true`. La prueba que cubría esto simulaba
      //     el modo avión cambiando esa misma propiedad, así que pasaba en
      //     verde mientras el teléfono hacía lo contrario.
      //
      // Juntos: teléfono en modo avión, token vencido, `getSession()` vuelve
      // con `null` enseguida, `navigator.onLine` dice `true` → a Login. Sin
      // señal para poder entrar, y con la credencial local intacta al lado.
      //
      // La regla ahora es una sola: **ante un `null`, si hay credencial local
      // usable se entra con ella.** No se le pregunta a nadie si hay red,
      // porque la pregunta no tiene una respuesta confiable en el único lugar
      // donde importa.
      //
      // Lo que se paga: una sesión revocada del lado del servidor —alguien
      // cerró sesión desde la web, o le cambiaron la contraseña— sigue
      // abriendo la aplicación hasta siete días. No es un agujero: el servidor
      // rechaza cada pedido igual, lo que se ve ya estaba descargado en el
      // teléfono, `SIGNED_OUT` borra la credencial en el acto, y la gracia le
      // pone techo. El otro error —dejar a alguien afuera en el campo— no
      // tiene techo ni arreglo desde donde está parado.
      const local = sesionOfflineUsable();
      if (local.ok) {
        setSession(local.session);
        setSinConexion(true);
        setMotivo(null);
        setGraciaHasta(venceLaGracia());
      } else {
        setSession(null);
        setSinConexion(false);
        setMotivo(local.motivo);
      }
      setLoading(false);
    };

    void resolver();

    const { data: sub } = supabase.auth.onAuthStateChange((evento, next) => {
      if (next) {
        // Cada refresco exitoso corre la gracia siete días más: quien usa la
        // aplicación con señal una vez por semana no la ve vencer nunca.
        guardarAutenticacionOnline(next);
        setSession(next);
        setSinConexion(false);
        setMotivo(null);
        setGraciaHasta(venceLaGracia());
        return;
      }

      // `SIGNED_OUT` es la única salida que borra la credencial local. Un
      // `TOKEN_REFRESH_FAILED` sin red NO puede hacerlo: es justo el caso para
      // el que existe el modo sin conexión.
      if (evento === 'SIGNED_OUT') {
        setSession(null);
        setSinConexion(false);
        return;
      }

      if (!navigator.onLine) {
        const local = sesionOfflineUsable();
        if (local.ok) {
          setSession(local.session);
          setSinConexion(true);
          return;
        }
      }
      setSession(null);
      setSinConexion(false);
    });

    return () => {
      cancelado = true;
      sub.subscription.unsubscribe();
    };
  }, [setSinConexion]);

  /**
   * Cuando vuelve la señal, la sesión local deja de alcanzar.
   *
   * Se revalida contra el servidor. Si el refresco anda, se sigue como
   * siempre. Si el servidor dice que no —contraseña cambiada, sesión revocada,
   * refresh token vencido— se va a Login. Lo que NO se toca es la cola: son
   * trabajos que no existen en ningún otro lado, y perderlos por tener que
   * volver a entrar sería el peor error posible de esta pantalla.
   */
  /**
   * Si en este momento se está trabajando con la credencial local.
   *
   * En una referencia y no leyendo el estado: el oyente de abajo se registra
   * una sola vez, y si dependiera del estado habría que volver a suscribirlo en
   * cada cambio.
   */
  const conLaLocal = useRef(false);
  // En un efecto y no durante el render: escribir una referencia mientras se
  // dibuja es un efecto secundario en medio de un cálculo, y React lo marca.
  useEffect(() => {
    conLaLocal.current = sinConexion;
  }, [sinConexion]);

  useEffect(() => {
    const alVolver = () => {
      // **Sólo se revalida si se estaba trabajando con la credencial local.**
      //
      // Esta función existe para un caso concreto: se venía sin señal, volvió,
      // y hay que comprobar si la sesión guardada sigue valiendo. Con una
      // sesión del servidor andando no hay nada que comprobar.
      //
      // Corría con CADA evento `online`, también estando perfectamente
      // conectado. Y el WebView de Android los emite cuando se le antoja, así
      // que corría al toque de entrar. Ahí `refreshSession()` puede contestar
      // 400 —Supabase rota el token de refresco, y pedir un refresco recién
      // entrado es pedir uno con el token anterior—, lo que esta pantalla leía
      // como «el servidor rechazó la sesión» y borraba la credencial.
      //
      // El síntoma era exactamente el que se vio en el teléfono: entrar con
      // señal, y que la línea de diagnóstico dijera «sin credencial · entré
      // hace 0 min». La sesión estaba viva; lo que se borraba era la copia que
      // permitía abrir sin señal después.
      if (!conLaLocal.current) return;

      void (async () => {
        const { data, error } = await supabase.auth.refreshSession();
        if (data.session) {
          guardarAutenticacionOnline(data.session);
          setSession(data.session);
          setSinConexion(false);
          setGraciaHasta(venceLaGracia());
          return;
        }
        // **Sólo un RECHAZO del servidor borra la credencial.** Un fallo de red
        // no.
        //
        // Antes acá decía `if (error)` a secas, con un comentario que afirmaba
        // «el servidor la rechazó estando disponible». El código no comprobaba
        // eso: cualquier error servía, y el más común de todos es que no haya
        // con quién hablar.
        //
        // Lo que pasaba en el teléfono: se entraba con señal y la credencial
        // quedaba guardada. Después llegaba un evento `online` sin que hubiera
        // red —en el WebView de Android ese evento llega cuando se le antoja, es
        // el mismo componente que informa `navigator.onLine = true` en modo
        // avión—, se intentaba refrescar, fallaba por red, y esta línea borraba
        // lo único que permitía abrir la aplicación sin señal.
        //
        // Al volver a abrir, la pantalla decía «en este teléfono todavía no
        // entraste nunca». Era cierto desde donde ella miraba, y por eso costó
        // una semana: el borrado no dejaba rastro, y todo lo demás funcionaba.
        if (elServidorRechazoLaSesion(error)) {
          // El servidor la rechazó estando disponible —contraseña cambiada,
          // sesión revocada, refresh token vencido—: la credencial ya no vale
          // nada y dejarla sería mantener abierta una sesión muerta.
          borrarSesionOffline('revalidacion');
          setSession(null);
          setSinConexion(false);
          setMotivo('sin-sesion-previa');
        }
      })();
    };

    window.addEventListener('online', alVolver);
    return () => window.removeEventListener('online', alVolver);
  }, [setSinConexion]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      sinConexion,
      motivo,
      graciaHasta,
      signOut: async () => {
        // El orden es deliberado: PRIMERO se mata lo local. Si se hiciera
        // después, cerrar sesión sin señal fallaría en la llamada al servidor y
        // el teléfono seguiría abriendo como la persona anterior.
        borrarSesionOffline('salir');
        localStorage.removeItem('gv.activeOrg');
        setSession(null);
        setSinConexion(false);

        // El caché de lecturas también se va: son datos de una organización que
        // este teléfono ya no tiene por qué mostrar. La COLA no: son trabajos
        // cargados que no existen en ningún otro lado, y se sincronizan cuando
        // esa misma persona vuelva a entrar.
        await borrarCacheDeLecturas();

        try {
          await supabase.auth.signOut();
        } catch {
          // Sin señal el servidor no se entera todavía, y no importa: la sesión
          // local ya está muerta y el token vence solo.
        }
      },
    }),
    [session, loading, sinConexion, motivo, graciaHasta, setSinConexion],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
